import classNames from "classnames";
import { FormControl, InputLabel, Select } from "@mui/material";
import { usePage } from "@inertiajs/inertia-react";

import { Trans } from "../utils/i18n";
import { ImagePlaceholder } from "./ImagePlaceholder";
import { formatCurrency } from "../utils/formatCurrency";
import PartMissingModal from "./PartMissingModal";
import {ProductModal} from './ProductModal';
import React, {useState} from 'react';

export function ProductCard({
    title,
    imgUrl,
    maxAmount = 1000,
    selectedAmount = 0,
    onAddToCart = () => {},
    onRemoveFromCart = () => {},
    onChangeAmount = () => {},
    priceGross,
    vat = 0,
    currency,
    children,
    className,
    hideProductDetailsButton = false,
    text,
}) {
    const isUnavailable =
        !maxAmount || maxAmount === 0 || !priceGross || !currency;
    const page = usePage();
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);

    return (
        <div className={classNames("flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden", className)}>
            {imgUrl ? (
                <img
                    src={imgUrl}
                    className="w-full h-48 object-contain bg-gray-900"
                />
            ) : (
                <div className="flex items-center justify-center w-full h-48 object-contain bg-gray-300">
                    <ImagePlaceholder />
                </div>
            )}

            <div className="flex-1 p-4 flex flex-col gap-2 items-center justify-center text-center">
                <div className="text-lg font-bold">{title}</div>

                {priceGross && currency && (
                    <div>
                        {formatCurrency(priceGross, currency)}{" "}
                        <span className="text-gray-500 font-normal">
                            (
                            <Trans
                                i18nKey="orderEntry.product.includedTax"
                                tax={vat / 100}
                            >
                                incl.
                                {{
                                    tax: vat / 100,
                                }}
                                %
                            </Trans>
                            )
                        </span>
                    </div>
                )}

                <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classNames(
                        (!selectedAmount || isUnavailable) && "!hidden"
                    )}
                    onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                    }}
                >
                    <InputLabel id="filter-client">
                        <Trans i18nKey="orderEntry.quantity.label">
                            Amount
                        </Trans>
                    </InputLabel>
                    <Select
                        labelId="filter-client"
                        label={
                            <Trans i18nKey="orderEntry.quantity.label">
                                Amount
                            </Trans>
                        }
                        size="small"
                        value={selectedAmount}
                        onChange={(e) => {
                            onChangeAmount(e.target.value);
                        }}
                        native
                    >
                        {[...Array(maxAmount).keys()].map((item) => {
                            // Start from quantity = 1
                            item += 1;
                            return (
                                <option key={String(item)} value={String(item)}>
                                    {String(item)}
                                </option>
                            );
                        })}
                    </Select>
                </FormControl>

                {isUnavailable && page.props.client.key === "amazon" ? (
                    <PartMissingModal productTitle={title} />
                ) : (
                    <button
                        type="button"
                        className={classNames(
                            "mt-2 no-underline block w-full font-bold bg-secondary rounded-md text-white px-9 py-3 hover:bg-white hover:text-secondary cursor-pointer shadow-lg transition-all",
                            {}
                        )}
                        disabled={isUnavailable}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();

                            if (selectedAmount === 0) {
                                return onAddToCart();
                            } else {
                                return onRemoveFromCart();
                            }
                        }}
                    >
                        {isUnavailable ? (
                            <Trans i18nKey="orderEntry.product.notAvailable">
                                Not available
                            </Trans>
                        ) : (
                            <>
                                {selectedAmount > 0 ? (
                                    <Trans i18nKey="common.remove">
                                        Remove
                                    </Trans>
                                ) : (
                                    <Trans i18nKey="common.add">Add</Trans>
                                )}
                            </>
                        )}
                    </button>
                )}

                {!hideProductDetailsButton && (
                    <button
                        type="button"
                        onClick={() => setIsProductModalOpen(true)}
                        className={classNames("mt-2 no-underline block w-full font-bold bg-white rounded-md text-secondary px-9 py-3  hover:bg-secondary hover:text-white cursor-pointer shadow-lg")}
                    >
                        <Trans i18nKey="orderEntry.product.viewDetails">View details</Trans>
                    </button>
                )}

                {children}
            </div>

            <ProductModal
                isOpen={isProductModalOpen}
                onClose={() => setIsProductModalOpen(false)}
                title={title}
                description={text}
                imgUrl={imgUrl}
                price={
                    <div>
                        {formatCurrency(
                            priceGross,
                            currency
                        )}{" "}
                        <span className="text-gray-500 font-normal">
                            (<Trans i18nKey="orderEntry.product.includedTax"
                                    tax={vat / 100}>incl. {{tax: vat / 100}}%</Trans>)
                        </span>
                    </div>
                }
            />

        </div>
    );
}

export function AmazonProductCard({ ...props }) {
    return (
        <ProductCard hideProductDetailsButton={true} {...props}>
            <div className="border-t border-gray-200 mt-2 pt-2 text-gray-500 text-sm">
                <div className="grid grid-cols-2 gap-2">
                    <div className="font-bold text-right">
                        <Trans i18nKey="productCard.dispatchedFrom">
                            Dispatched from
                        </Trans>
                    </div>
                    <div className="text-left">
                        <Trans i18nKey="common.germany">Germany</Trans>
                    </div>

                    <div className="font-bold text-right">
                        <Trans i18nKey="productCard.soldBy">Sold by</Trans>
                    </div>
                    <div className="text-left">Arvato SE</div>

                    <div className="font-bold text-right">
                        <Trans i18nKey="productCard.returns">Returns</Trans>
                    </div>
                    <div className="text-left">
                        <a
                            className="underline text-primary"
                            href={route("shop.termsandconditions")}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Trans i18nKey="common.terms">Terms & Conditions</Trans>
                        </a>
                    </div>

                    <div className="font-bold text-right">
                        <Trans i18nKey="productCard.paymentTo">
                            Payment to
                        </Trans>
                    </div>
                    <div className="text-left">
                        Arvato SE, secure transaction
                    </div>
                </div>
            </div>
        </ProductCard>
    );
}
